import { useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useCountriesCoverageQuery } from 'src/context/api/filters/api';
import { fDate } from 'src/utils/format-time';
import { CountryItem } from 'src/components/country-bar/types';
import { deriveCountryCode } from './countryCode';

// Import du contexte pour vérifier s'il existe
import { VisibilityContext } from '../components/VisibilityProvider';

export const useCountryData = () => {
    // Essayer d'utiliser le contexte si disponible
    const context = useContext(VisibilityContext);

    // Si le contexte est disponible, l'utiliser
    if (context) {
        return {
            countriesData: context.countriesData,
            isLoading: context.isLoadingBaseData,
        };
    }

    // Sinon, fallback vers l'ancien comportement (standalone).
    // Auparavant la liste pays était dérivée de useGetVisibilityKpisQuery (payload
    // complet des cartes Visibility via main_overview.sql) : la CountryBar attendait
    // une grosse requête analytics juste pour afficher ~10 onglets. On tape désormais
    // un endpoint léger (/filters/countries-coverage) qui renvoie directement
    // region_name + brand_traffic_share depuis traffic_aggregated.
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.bqTable;

    const { data: coverageData, isFetching: isLoadingBaseData } = useCountriesCoverageQuery({
        workspace: workspace?.bqTable,
        platforms: filterStore?.platforms,
        devices: filterStore?.devices,
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
    }, {
        skip: skipQuery,
    });

    const rows: any[] = useMemo(
        () => (Array.isArray(coverageData?.countries) ? coverageData.countries : []),
        [coverageData]
    );

    // Le backend trie déjà par couverture desc ; on re-trie côté client par sécurité
    // (le tri est le seul consommateur de share_of_brand_searches : le % n'est plus affiché).
    const countriesData: CountryItem[] = useMemo(() => {
        return rows
            .map((row: any) => ({
                country_name: row.region_name,
                country_code: deriveCountryCode(row.region_name),
                share_of_brand_searches:
                    typeof row.brand_traffic_share === 'number' ? row.brand_traffic_share : null,
            }))
            .sort((a, b) => (b.share_of_brand_searches ?? 0) - (a.share_of_brand_searches ?? 0));
    }, [rows]);

    return {
        countriesData,
        isLoading: isLoadingBaseData,
    };
};
